import React, { useEffect, useState } from 'react';
import {
  getCurrentAccountId,
  getCurrentAccountName,
  getCurrentUserEmail,
  getLogoutUrl,
} from '../../../utils/tokenUtils.ts';
import cn from 'classnames';
import { useAppSelector } from '../../../redux/store.ts';
import { getPinnedAccounts } from '../../../utils/pinAccounts.ts';
import type { Account } from '../../../redux/types.ts';
import { deleteAccountToken, deleteUserToken, isAuth } from '../../../utils/tokenServices.ts';
import { USERS_API_URL } from '../../../constants.ts';
import styles from './userDropdown.module.scss';
import { useTranslation } from 'react-i18next';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import Popper from '@mui/material/Popper';
import UserDropdownButton from './UserDropdownButton.tsx';
import AccountItems from './AccountItems.tsx';
import UserItems from './UserItems.tsx';
import { SignOutIcon } from '@phosphor-icons/react/dist/csr/SignOut';
import { UsersIcon } from '@phosphor-icons/react/dist/csr/Users';

const {
  userMenuContainer,
  userMenuDivider,
  userMenuItem,
  iconLogout,
  iconUser,
  userMenuActive,
  userLogin,
  userLoginDisabled,
  text,
  userDropdownWrapper,
  userDropdownWrapperActive,
  userMenuHeader,
} = styles;

const UserDropdown = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'userDropdown' });
  const [isLoginLoading, setIsLoading] = useState(false);
  const [pinnedAccounts, setPinnedAccounts] = useState<{ accountId: number; name: string }[]>(getPinnedAccounts());
  const frameworkMode = useAppSelector(state => state.layout.frameworkMode);

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = React.useRef<HTMLDivElement | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);

  const isAccountMode = frameworkMode === 'ACCOUNT';
  const { pathname } = window.location;

  const getTitle = (account?: Account) => {
    let title = getCurrentUserEmail() ?? 'User';

    if (account) {
      return title + ` | ${account.accountId} - ${account.name}`;
    }

    if (isAccountMode) {
      title += ` | ${getCurrentAccountId()} - ${getCurrentAccountName()}`;
    }
    return title;
  };

  const title = getTitle();

  const onAccountChanges = () => {
    setPinnedAccounts(getPinnedAccounts());
  };

  const onClose = () => {
    setAnchorEl(null);
    setIsOpen(false);
  };

  const handleClose = (e: Event) => {
    const path = e.composedPath();

    if (!dropdownRef.current || !buttonRef.current) {
      return;
    }

    if (path.includes(dropdownRef.current) || path.includes(buttonRef.current)) {
      return;
    }

    onClose();
  };

  useEffect(() => {
    window.addEventListener('ACCOUNT_CHANGES', onAccountChanges);
    window.addEventListener('click', handleClose);

    return () => {
      window.removeEventListener('ACCOUNT_CHANGES', onAccountChanges);
      window.removeEventListener('click', handleClose);
    };
  }, []);

  const onToggle = () => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  };

  const onOpen = () => {
    setPinnedAccounts(getPinnedAccounts());
    setAnchorEl(buttonRef.current);
    setIsOpen(true);
  };

  const onChangeAccount = (account: { accountId: number; name: string }) => {
    window.dispatchEvent(new CustomEvent('framework-change-Account', { detail: account }));
    onClose();
  };

  const onRedirectToSelectAccount = () => {
    window.dispatchEvent(new CustomEvent('framework-redirect-SelectAccount'));
    onClose();
  };

  const onRedirectToCreateAccount = () => {
    window.dispatchEvent(new CustomEvent('framework-redirect-CreateAccount'));
    onClose();
  };

  const backToUserMode = () => {
    window.dispatchEvent(new CustomEvent('framework-back-to-UserMode'));
    onClose();
  };

  const openProfile = () => {
    window.dispatchEvent(new CustomEvent('framework-open-profile'));
    onClose();
  };

  const onLogin = () => {
    window.dispatchEvent(new CustomEvent('framework-set-loader', { detail: true }));
    setIsLoading(true);
    window.location.href = `${USERS_API_URL}/sso/redirect`;
  };

  const logout = () => {
    const logoutUrl = getLogoutUrl() ?? '/';
    deleteAccountToken();
    deleteUserToken();
    const opened = window.open(logoutUrl, '_blank');
    if (opened) {
      setTimeout(() => {
        opened.close();
        window.location.replace('/');
      }, 1000);
    }
  };

  if (!isAuth()) {
    return (
      <div>
        <div
          className={cn(userLogin, { [userLoginDisabled]: isLoginLoading })}
          onClick={isLoginLoading ? () => {} : onLogin}
        >
          <PersonOutlineOutlinedIcon className={iconUser} />
          <div className={text}>{t('login')}</div>
        </div>
      </div>
    );
  }

  return (
    <div id="user-dropdown-wrapper" className={cn(userDropdownWrapper, { [userDropdownWrapperActive]: isOpen })}>
      <UserDropdownButton buttonRef={buttonRef} title={title} isOpen={isOpen} onToggle={onToggle} />
      <Popper anchorEl={anchorEl} placement="bottom-end" id="user-menu" disablePortal open={isOpen}>
        <div className={userMenuContainer} ref={dropdownRef}>
          <div className={userMenuHeader}>{t('headerTitle')}</div>
          <div className={cn(userMenuItem, { [userMenuActive]: pathname === '/users' })} onClick={openProfile}>
            <UsersIcon size={24} className={iconUser} />
            {t('profile')}
          </div>
          {isAccountMode ? (
            <AccountItems
              backToUserMode={backToUserMode}
              onRedirectToCreateAccount={onRedirectToCreateAccount}
              onRedirectToSelectAccount={onRedirectToSelectAccount}
              onChangeAccount={onChangeAccount}
              pinnedAccounts={pinnedAccounts}
            />
          ) : (
            <UserItems
              onRedirectToCreateAccount={onRedirectToCreateAccount}
              onRedirectToSelectAccount={onRedirectToSelectAccount}
              onChangeAccount={onChangeAccount}
              pinnedAccounts={pinnedAccounts}
            />
          )}
          <div className={userMenuDivider}></div>
          <div className={userMenuItem} onClick={logout}>
            <SignOutIcon size={24} className={iconLogout} />
            {t('logout')}
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default UserDropdown;
